import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { useAuth } from "../context/AuthContext";
import { Cake, Trophy, X } from "lucide-react";
import { Button } from "./ui/button";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const fireConfetti = () => {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ["#ec4899", "#f59e0b", "#3b82f6", "#10b981", "#a855f7"];

  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 70,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 70,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

const CelebrationBanner = () => {
  const { user } = useAuth();
  const [celebration, setCelebration] = useState(null); // {type, title, message}
  const firedRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayMMDD = `${mm}-${dd}`;
    const todayISO = today.toISOString().slice(0, 10);

    // Build list of eligible celebrations for the logged-in user today
    const events = [];

    if (user.birthday === todayMMDD) {
      events.push({
        type: "birthday",
        title: `Happy Birthday, ${user.name?.split(" ")[0] || "friend"}!`,
        message: "Wishing you a fantastic year ahead from the SMJ family 🎂",
      });
    }

    if (user.hire_date && user.hire_date.length === 10) {
      const [hy, hm, hd] = user.hire_date.split("-");
      if (hm === mm && hd === dd) {
        const years = today.getFullYear() - parseInt(hy, 10);
        if (years > 0) {
          events.push({
            type: "anniversary",
            title: `Happy ${ordinal(years)} Work Anniversary!`,
            message: `Thank you for ${years} amazing ${
              years === 1 ? "year" : "years"
            } at the firm.`,
          });
        }
      }
    }

    if (events.length === 0) return;

    // One-shot per user per day guard (handles "still logged in from yesterday").
    // Only counts as "seen" when the user explicitly dismisses — otherwise we
    // show again on refresh so they don't miss their day.
    const storageKey = `celebration_dismissed_${user.id}_${todayISO}`;
    if (typeof window !== "undefined" && window.localStorage.getItem(storageKey)) {
      return;
    }

    // Show the first (birthday takes priority if both fall today)
    const primary = events[0];
    setCelebration(primary);

    if (!firedRef.current) {
      firedRef.current = true;
      // slight delay so the banner is visible first
      setTimeout(fireConfetti, 250);
    }
  }, [user?.id, user?.birthday, user?.hire_date, user?.name]);

  const handleDismiss = () => {
    if (user?.id && typeof window !== "undefined") {
      const todayISO = new Date().toISOString().slice(0, 10);
      window.localStorage.setItem(
        `celebration_dismissed_${user.id}_${todayISO}`,
        "1"
      );
    }
    setCelebration(null);
  };

  if (!celebration) return null;

  const Icon = celebration.type === "birthday" ? Cake : Trophy;
  const gradient =
    celebration.type === "birthday"
      ? "from-pink-500 via-fuchsia-500 to-amber-400"
      : "from-amber-500 via-orange-500 to-rose-500";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-5 sm:p-6 shadow-lg bg-gradient-to-r ${gradient} text-white animate-fade-in`}
      data-testid="celebration-banner"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
          <Icon size={26} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl sm:text-2xl font-bold">{celebration.title}</h3>
          <p className="text-sm sm:text-base text-white/90 mt-1">
            {celebration.message}
          </p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="text-white hover:bg-white/20 shrink-0"
          onClick={handleDismiss}
          data-testid="celebration-banner-close"
          aria-label="Dismiss celebration"
        >
          <X size={18} />
        </Button>
      </div>
      {/* decorative circles */}
      <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -bottom-12 -left-6 w-32 h-32 rounded-full bg-white/10" />
    </div>
  );
};

export default CelebrationBanner;
